class MetafieldSearchIndex {
  constructor() {
    this.products = null;
    this.loadPromise = null;
  }

  load() {
    if (this.products) return Promise.resolve(this.products);
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = this.fetchAllPages().then((products) => {
      this.products = products;
      return products;
    });

    return this.loadPromise;
  }

  async fetchAllPages() {
    const products = [];
    let page = 1;
    let totalPages = 1;

    // Section Rendering API → sections/search-metafield-api.liquid → snippets/search-metafield-index.liquid
    const rootUrl = window.routes?.root_url || '/';
    while (page <= totalPages) {
      const response = await fetch(`${rootUrl}?section_id=search-metafield-api&page=${page}`);
      if (!response.ok) break;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const dataElement = doc.getElementById('search-metafield-index-data');
      if (!dataElement) break;

      const data = JSON.parse(dataElement.textContent);
      products.push(...(data.products || []));
      totalPages = data.pages || 1;
      page += 1;
    }

    return products;
  }

  async search(query) {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return [];

    const products = await this.load();
    return products.filter((product) => product.searchText.includes(normalizedQuery));
  }
}

class MetafieldSearchUI {
  constructor() {
    this.index = new MetafieldSearchIndex();
    this.augmenting = false;
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    document.querySelectorAll('predictive-search').forEach((element) => {
      const resultsContainer = element.querySelector('[data-predictive-search]');
      if (!resultsContainer) return;

      const observer = new MutationObserver(() => {
        const query = element.querySelector('input[type="search"]')?.value?.trim();
        if (!query) return;
        this.augmentPredictiveSearch(element, query);
      });

      observer.observe(resultsContainer, { childList: true, subtree: true });
    });

    const mainSearchGrid = document.querySelector('[data-metafield-search-query]');
    if (mainSearchGrid) {
      this.augmentMainSearch(mainSearchGrid);
    }
  }

  getExistingProductHandles(container) {
    const handles = new Set();
    container.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      const match = link.href.match(/\/products\/([^/?#]+)/);
      if (match) handles.add(match[1]);
    });
    return handles;
  }

  buildPredictiveSearchItem(product, index) {
    const imageMarkup = product.image
      ? `<img class="predictive-search__image" src="${this.escapeHtml(product.image)}" alt="${this.escapeHtml(product.title)}" width="50" height="50" loading="lazy">`
      : '';

    return `
      <li
        id="predictive-search-option-metafield-product-${index}"
        class="predictive-search__list-item"
        role="option"
        aria-selected="false"
        data-metafield-search-result="true"
      >
        <a
          href="${this.escapeHtml(product.url)}"
          class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text"
          tabindex="-1"
        >
          ${imageMarkup}
          <div class="predictive-search__item-content predictive-search__item-content--centered">
            <p class="predictive-search__item-heading h5">${this.escapeHtml(product.title)}</p>
          </div>
        </a>
      </li>
    `;
  }

  buildMainSearchItem(product) {
    const title = this.escapeHtml(product.title);
    const url = this.escapeHtml(product.url);

    return `
      <a class="index-second__swatch" href="${url}" data-metafield-search-result="true">
        <div class="index-second__swatch-title">${title}</div>
      </a>
    `;
  }

  async fetchProductCardHtml(product) {
    try {
      const url = new URL(product.url, window.location.origin);
      url.searchParams.set('section_id', 'search-metafield-card');
      const response = await fetch(url);
      if (!response.ok) return this.buildMainSearchItem(product);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const item = doc.querySelector('[data-metafield-search-result]');
      return item ? item.outerHTML : this.buildMainSearchItem(product);
    } catch (error) {
      console.error('Failed to fetch product card:', product.handle, error);
      return this.buildMainSearchItem(product);
    }
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  ensurePredictiveProductList(resultsContainer) {
    let list = resultsContainer.querySelector('#predictive-search-results-products-list');
    if (list) return list;

    const resultsRoot = resultsContainer.querySelector('#predictive-search-results');
    if (!resultsRoot) return null;

    let groupsWrapper = resultsContainer.querySelector('#predictive-search-results-groups-wrapper');
    if (!groupsWrapper) {
      groupsWrapper = document.createElement('div');
      groupsWrapper.id = 'predictive-search-results-groups-wrapper';
      groupsWrapper.className = 'predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions';
      resultsRoot.insertBefore(groupsWrapper, resultsRoot.firstChild);
    }

    let resultGroup = groupsWrapper.querySelector('.predictive-search__result-group');
    if (!resultGroup) {
      resultGroup = document.createElement('div');
      resultGroup.className = 'predictive-search__result-group';
      groupsWrapper.appendChild(resultGroup);
    }

    const productsBlock = document.createElement('div');
    productsBlock.innerHTML = `
      <h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">Products</h2>
      <ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products"></ul>
    `;
    resultGroup.appendChild(productsBlock);
    list = productsBlock.querySelector('#predictive-search-results-products-list');
    return list;
  }

  async augmentPredictiveSearch(predictiveSearchElement, query) {
    if (this.augmenting) return;

    const resultsContainer = predictiveSearchElement.querySelector('[data-predictive-search]');
    if (!resultsContainer) return;

    const resultsRoot = resultsContainer.querySelector('#predictive-search-results');
    if (!resultsRoot) return;

    const list = this.ensurePredictiveProductList(resultsContainer);
    if (!list) return;
    if (list.dataset.metafieldAugmented === query) return;

    this.augmenting = true;

    try {
      const matches = await this.index.search(query);
      const existingHandles = this.getExistingProductHandles(resultsContainer);
      list.querySelectorAll('[data-metafield-search-result="true"]').forEach((item) => item.remove());

      const newProducts = matches.filter((product) => !existingHandles.has(product.handle)).slice(0, 5);
      if (newProducts.length > 0) {
        const markup = newProducts
          .map((product, index) => this.buildPredictiveSearchItem(product, index + 1))
          .join('');
        list.insertAdjacentHTML('beforeend', markup);
      }

      list.dataset.metafieldAugmented = query;
      if (newProducts.length > 0 || matches.length > 0) {
        predictiveSearchElement.setAttribute('results', 'true');
        predictiveSearchElement.setAttribute('open', 'true');
      }
    } finally {
      this.augmenting = false;
    }
  }

  async augmentMainSearch(gridContainer) {
    const query = gridContainer.dataset.metafieldSearchQuery;
    if (!query) return;

    const noResultsMessage = document.getElementById('MetafieldSearchNoResults');

    let matches;
    try {
      matches = await this.index.search(query);
    } catch (error) {
      console.error('Metafield search failed:', error);
      if (noResultsMessage) noResultsMessage.hidden = false;
      return;
    }

    const existingHandles = this.getExistingProductHandles(gridContainer);
    const newProducts = matches.filter((product) => !existingHandles.has(product.handle));

    if (newProducts.length === 0) {
      if (noResultsMessage) noResultsMessage.hidden = false;
      return;
    }

    if (noResultsMessage) noResultsMessage.hidden = true;

    const emptyContainer = gridContainer.closest('.template-search--empty');
    if (emptyContainer) emptyContainer.classList.remove('template-search--empty');

    let resultsWrapper = gridContainer.querySelector('.template-search__results');
    if (!resultsWrapper) {
      resultsWrapper = document.createElement('div');
      resultsWrapper.className = 'template-search__results collection search-index-second page-width';
      gridContainer.appendChild(resultsWrapper);
    }

    let grid = resultsWrapper.querySelector('#product-grid');
    if (!grid) {
      grid = document.createElement('div');
      grid.id = 'product-grid';
      grid.className = gridContainer.dataset.productGridClass || 'index-second__grid';
      grid.dataset.id =
        gridContainer.dataset.sectionId ||
        gridContainer.querySelector('#product-grid[data-id]')?.dataset.id ||
        document.getElementById('main-search-filters')?.dataset.id ||
        '';
      resultsWrapper.appendChild(grid);
    }

    const collection = resultsWrapper.closest('.collection');
    if (collection) collection.classList.remove('loading');

    resultsWrapper.querySelector('.loading-overlay')?.remove();

    const cardHtmlList = await Promise.all(newProducts.map((product) => this.fetchProductCardHtml(product)));
    const markup = cardHtmlList.filter(Boolean).join('');
    grid.insertAdjacentHTML('beforeend', markup);
  }
}

new MetafieldSearchUI();
